import { GetSubscriptionPlansResponse } from "@/redux/apis/subscription/types";
import { FeatureSection, FeatureValue, PlanConfig } from "./types";

// Helper functions to create feature values
const available = (): FeatureValue => ({ type: "boolean", available: true });
const unavailable = (): FeatureValue => ({ type: "boolean", available: false });
const text = (content: string): FeatureValue => ({ type: "text", content });
const badge = (label: string, variant: "info" | "primary" = "info"): FeatureValue => ({
  type: "badge",
  label,
  variant,
});

// Feature display overrides - maps feature names to custom display values per subscription slug
// This is needed because the API doesn't include marketing/display text
// Keys are feature names, values are maps of subscription slug -> display value
const FEATURE_DISPLAY_OVERRIDES: Record<string, Record<string, FeatureValue>> = {
  "Company Aliases (DBA)": {
    "solo-provider-plan": text("Paid Feature:\n$59/mo for Unlimited Aliases"),
    "basic-provider-plan": text("One Alias"),
    "enterprise-provider-plan": text("Unlimited Aliases"),
  },
  "Lead Quote Close (LQC)": {
    "solo-provider-plan": text("Paid Feature:\n1% LQC Bookings ($300/mo minimum)"),
    "basic-provider-plan": text("Paid Feature:\n1% LQC Bookings ($300/mo minimum)"),
    "enterprise-provider-plan": text("Paid Feature:\n1% LQC Bookings (no monthly minimum)"),
  },
  "Partner Connectivity (e.g. GRIDD)": {
    "solo-provider-plan": text("Paid Feature:\nFree/Tip"),
    "basic-provider-plan": text("Paid Feature:\nFree/Tip"),
    "enterprise-provider-plan": text("Paid Feature:\nFree/Tip"),
  },
  "Shuttle Module": {
    "solo-provider-plan": text("Paid Feature:\n$1 + 2.5%/ticket ($100/mo minimum)"),
    "basic-provider-plan": text("Paid Feature:\n$1 + 2.5%/ticket ($100/mo minimum)"),
    "enterprise-provider-plan": text("Paid Feature:\n$1 + 2.5%/ticket (no monthly minimum)"),
  },
  "Passenger App (PWA)": {
    "solo-provider-plan": text("Paid Feature:\n$199 + $99/mo"),
    "basic-provider-plan": text("Paid Feature:\n$199 + $99/mo"),
    // enterprise: available (from API)
  },
  "Messaging Manager": {
    "solo-provider-plan": text("Paid Feature:\n$39/mo"),
    // fleet: available (from API)
    // enterprise: available (from API)
  },
  "Advanced Business Intelligence": {
    "enterprise-provider-plan": badge("Coming Soon", "primary"),
  },
  "Mobile-Responsive Website with Integrated Email and Hosting": {
    "solo-provider-plan": text("Paid Feature:\n$199 + $29/mo"),
    // fleet: available (from API)
    // enterprise: available (from API)
  },
  "Additional Support": {
    "basic-provider-plan": badge("Prioritized"),
    "enterprise-provider-plan": badge("Premium"),
  },
};

/**
 * Transforms API subscription plans to UI plan configs
 * Creates one column per subscription from the API
 */
export const transformSubscriptionPlans = (apiData: GetSubscriptionPlansResponse): PlanConfig[] => {
  return apiData.subscriptions.map((sub) => ({
    id: sub.subscriptionId,
    name: sub.subscriptionName,
    iconSvg: sub.iconSvg,
    fee: sub.subscriptionFee,
    setupFee: sub.setupFee,
    description: sub.subscriptionDescription,
    audience: sub.subscriptionAudience,
    categorySlug: sub.subscriptionCategorySlug,
  }));
};

/**
 * Checks if a defaultValue should be displayed as text or treated as a boolean
 * Per CEO/CTO guidance: We should never display true/false/-/yes/no as text
 * Valid display values are: numbers, "Unlimited", "Coming Soon", "Paid", descriptive text, etc.
 */
export const isDisplayableValue = (value: string): boolean => {
  const lowerValue = value.toLowerCase().trim();
  // These values should NOT be displayed as text - they indicate boolean state
  const booleanIndicators = ["true", "false", "-", "yes", "no"];
  return !booleanIndicators.includes(lowerValue);
};

/**
 * Gets the display override for a specific feature and subscription, if one exists.
 * Used to apply consistent marketing/display text across all feature displays.
 */
export const getFeatureDisplayOverride = (featureName: string, subscriptionId: string): FeatureValue | undefined => {
  return FEATURE_DISPLAY_OVERRIDES[featureName]?.[subscriptionId];
};

/**
 * Extracts all features available for a specific plan from subscription data
 * The presence of a subscription in the feature's subscriptions array means the feature is available
 * Applies FEATURE_DISPLAY_OVERRIDES for consistent marketing/display text
 */
export const extractPlanFeatures = (subscriptionData: GetSubscriptionPlansResponse, planId: string): string[] => {
  const features: string[] = [];

  // Sort feature groups by display order
  const sortedGroups = [...subscriptionData.featureGroups].sort((a, b) => a.displayOrder - b.displayOrder);

  for (const group of sortedGroups) {
    // Sort features by display order
    const sortedFeatures = [...group.features].sort((a, b) => a.displayOrder - b.displayOrder);

    for (const feature of sortedFeatures) {
      // Check if this plan has this feature (presence means it's available)
      const planFeature = feature.subscriptions.find((sub) => sub.subscriptionId === planId);

      if (planFeature) {
        // Check for display override first (marketing/pricing text)
        const override = getFeatureDisplayOverride(feature.featureName, planId);
        if (override) {
          if (override.type === "text") {
            features.push(`${feature.featureName} — ${override.content.replace(/\n/g, " ")}`);
          } else if (override.type === "badge") {
            features.push(`${feature.featureName} — ${override.label}`);
          } else {
            features.push(feature.featureName);
          }
        } else if (planFeature.defaultValue && isDisplayableValue(planFeature.defaultValue)) {
          // Has a meaningful display value (number, "Unlimited", etc.) - show it with the feature name
          features.push(`${planFeature.defaultValue} ${feature.featureName}`);
        } else {
          // Feature is simply available (no special value to display)
          features.push(feature.featureName);
        }
      }
    }
  }

  return features;
};

/**
 * Transforms API feature groups to UI feature sections
 * Now uses subscription IDs directly instead of plan types
 *
 * Display logic:
 * 1. If a subscription has the feature in its subscriptions array → it's available
 * 2. If defaultValue exists and is a displayable value (not true/false/-) → show as text
 * 3. Otherwise → show checkmark for available, X for unavailable
 * 4. Display overrides take precedence for marketing/display text
 */
export const transformFeatureSections = (apiData: GetSubscriptionPlansResponse, allSubscriptionIds: string[]): FeatureSection[] => {
  // Create a copy since RTK Query data is immutable
  return [...apiData.featureGroups]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((group) => ({
      title: group.groupName,
      features: [...group.features]
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((feature) => {
          // Initialize all subscriptions as unavailable
          const values: Record<string, FeatureValue> = {};
          allSubscriptionIds.forEach((id) => {
            values[id] = unavailable();
          });

          // Process each subscription that has this feature
          // The presence of a subscription in the feature's subscriptions array means it's available
          feature.subscriptions.forEach((sub) => {
            if (sub.defaultValue && isDisplayableValue(sub.defaultValue)) {
              // Has a meaningful display value (number, "Unlimited", etc.)
              values[sub.subscriptionId] = text(sub.defaultValue);
            } else {
              // Feature is available for this subscription (show checkmark)
              values[sub.subscriptionId] = available();
            }
          });

          // Apply display overrides if they exist for this feature
          // Overrides take precedence over API values for marketing/display purposes
          const overrides = FEATURE_DISPLAY_OVERRIDES[feature.featureName];
          if (overrides) {
            Object.entries(overrides).forEach(([subscriptionId, displayValue]) => {
              // Only apply if this subscription exists in our list
              if (allSubscriptionIds.includes(subscriptionId)) {
                values[subscriptionId] = displayValue;
              }
            });
          }

          return {
            name: feature.featureName,
            hasInfo: !!feature.featureDescription,
            description: feature.featureDescription,
            values,
          };
        }),
    }));
};
